export type BattleshipAttackStyle =
  | "carrier"
  | "battleship"
  | "cruiser"
  | "submarine"
  | "destroyer";

let sharedAudioContext: AudioContext | null = null;
let effectsBus: GainNode | null = null;
let musicBus: GainNode | null = null;
let musicSources: AudioScheduledSourceNode[] = [];
const activeEffectMasters = new Set<GainNode>();
let battleshipAudioEnabled = true;

function audioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  sharedAudioContext ??= new AudioContext();
  return sharedAudioContext;
}

export async function primeBattleshipAudio(): Promise<void> {
  const context = audioContext();
  if (context?.state === "suspended") await context.resume();
}

function getEffectsBus(context: AudioContext): GainNode {
  if (effectsBus) return effectsBus;
  effectsBus = context.createGain();
  effectsBus.gain.value = 0.82;
  effectsBus.connect(context.destination);
  return effectsBus;
}

function tone(
  context: AudioContext,
  output: AudioNode,
  start: number,
  duration: number,
  fromFrequency: number,
  toFrequency: number,
  volume: number,
  type: OscillatorType = "sine",
) {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(Math.max(1, fromFrequency), start);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, toFrequency), start + duration);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + Math.min(0.025, duration * 0.15));
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain);
  gain.connect(output);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

function noise(
  context: AudioContext,
  output: AudioNode,
  start: number,
  duration: number,
  volume: number,
  filterFrequency: number,
  filterType: BiquadFilterType = "lowpass",
) {
  const frameCount = Math.max(1, Math.floor(context.sampleRate * duration));
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let index = 0; index < samples.length; index += 1) samples[index] = Math.random() * 2 - 1;
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  source.buffer = buffer;
  filter.type = filterType;
  filter.frequency.setValueAtTime(filterFrequency, start);
  gain.gain.setValueAtTime(volume, start);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  source.connect(filter);
  filter.connect(gain);
  gain.connect(output);
  source.start(start);
}

function breakAndSink(context: AudioContext, output: AudioNode, start: number) {
  noise(context, output, start, 0.42, 0.13, 1_900, "highpass");
  tone(context, output, start, 0.7, 210, 46, 0.095, "sawtooth");
  for (let index = 0; index < 4; index += 1)
    tone(
      context,
      output,
      start + index * 0.12,
      0.22,
      760 - index * 90,
      160 - index * 18,
      0.035,
      "square",
    );
  noise(context, output, start + 0.48, 1.2, 0.12, 720);
  tone(context, output, start + 0.5, 1.35, 82, 24, 0.12, "sine");
  tone(context, output, start + 0.78, 0.55, 210, 74, 0.04, "sine");
  tone(context, output, start + 1.08, 0.62, 170, 58, 0.035, "sine");
}

function startBackgroundMusic(context: AudioContext) {
  if (musicBus || context.state !== "running") return;
  const bus = context.createGain();
  const filter = context.createBiquadFilter();
  bus.gain.setValueAtTime(0.0001, context.currentTime);
  bus.gain.exponentialRampToValueAtTime(0.034, context.currentTime + 1.4);
  filter.type = "lowpass";
  filter.frequency.value = 520;
  bus.connect(filter);
  filter.connect(context.destination);

  const sources: AudioScheduledSourceNode[] = [];
  for (const [frequency, volume, type] of [
    [55, 0.34, "sine"],
    [65.41, 0.19, "triangle"],
    [82.41, 0.13, "sine"],
  ] as const) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.value = volume;
    oscillator.connect(gain);
    gain.connect(bus);
    oscillator.start();
    sources.push(oscillator);
  }

  const waveBuffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate);
  const waveSamples = waveBuffer.getChannelData(0);
  for (let index = 0; index < waveSamples.length; index += 1)
    waveSamples[index] =
      (Math.random() * 2 - 1) *
      (0.35 + Math.sin((index / context.sampleRate) * Math.PI * 0.7) * 0.18);
  const waves = context.createBufferSource();
  const waveGain = context.createGain();
  const waveFilter = context.createBiquadFilter();
  waves.buffer = waveBuffer;
  waves.loop = true;
  waveGain.gain.value = 0.08;
  waveFilter.type = "lowpass";
  waveFilter.frequency.value = 280;
  waves.connect(waveFilter);
  waveFilter.connect(waveGain);
  waveGain.connect(bus);
  waves.start();
  sources.push(waves);

  const pulse = context.createOscillator();
  const pulseDepth = context.createGain();
  pulse.frequency.value = 0.11;
  pulseDepth.gain.value = 0.007;
  pulse.connect(pulseDepth);
  pulseDepth.connect(bus.gain);
  pulse.start();
  sources.push(pulse);

  musicBus = bus;
  musicSources = sources;
}

function stopBackgroundMusic(context: AudioContext) {
  const bus = musicBus;
  const sources = musicSources;
  if (!bus) return;
  bus.gain.cancelScheduledValues(context.currentTime);
  bus.gain.setValueAtTime(Math.max(0.0001, bus.gain.value), context.currentTime);
  bus.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.28);
  musicBus = null;
  musicSources = [];
  window.setTimeout(() => {
    for (const source of sources) {
      try {
        source.stop();
      } catch {
        // A source can already be stopped during rapid mute/unmute toggles.
      }
    }
    bus.disconnect();
  }, 320);
}

export async function setBattleshipAudioEnabled(enabled: boolean): Promise<void> {
  try {
    battleshipAudioEnabled = enabled;
    const context = audioContext();
    if (!context) return;
    if (!enabled) {
      if (effectsBus) effectsBus.gain.setTargetAtTime(0.0001, context.currentTime, 0.025);
      for (const master of activeEffectMasters) {
        master.gain.cancelScheduledValues(context.currentTime);
        master.gain.setValueAtTime(0.0001, context.currentTime);
        try {
          master.disconnect();
        } catch {
          // A completed effect may already have released its output.
        }
      }
      activeEffectMasters.clear();
      stopBackgroundMusic(context);
      return;
    }
    await primeBattleshipAudio();
    if (context.state !== "running") return;
    const effects = getEffectsBus(context);
    effects.gain.cancelScheduledValues(context.currentTime);
    effects.gain.setTargetAtTime(0.82, context.currentTime, 0.025);
    startBackgroundMusic(context);
  } catch {
    // Autoplay policies can keep audio suspended until the next game interaction.
  }
}

function impact(
  context: AudioContext,
  output: AudioNode,
  start: number,
  result: "hit" | "miss",
  strength = 1,
) {
  if (result === "hit") {
    noise(context, output, start, 0.48, 0.16 * strength, 1_300);
    tone(context, output, start, 0.55, 105, 38, 0.16 * strength, "sine");
    tone(context, output, start + 0.015, 0.24, 420, 92, 0.06 * strength, "sawtooth");
    return;
  }
  noise(context, output, start, 0.58, 0.09 * strength, 2_800);
  tone(context, output, start, 0.46, 620, 170, 0.055 * strength, "sine");
}

export async function playBattleshipAttackSound(
  style: BattleshipAttackStyle,
  result: "hit" | "miss",
  sunk: boolean,
): Promise<void> {
  try {
    if (!battleshipAudioEnabled) return;
    const context = audioContext();
    if (!context) return;
    if (context.state === "suspended") await context.resume();
    if (context.state !== "running") return;

    const master = context.createGain();
    master.gain.value = 0.62;
    master.connect(getEffectsBus(context));
    activeEffectMasters.add(master);
    const now = context.currentTime + 0.025;
    let impactDelay = 1.06;
    let impactStrength = 1;

    if (style === "carrier") {
      for (let index = 0; index < 3; index += 1) {
        const start = now + index * 0.13;
        tone(context, master, start, 0.8, 190 + index * 24, 610, 0.035, "sawtooth");
        noise(context, master, start, 0.52, 0.022, 4_500);
      }
      impactDelay = 1.44;
      impactStrength = 1.08;
    } else if (style === "battleship") {
      noise(context, master, now, 0.14, 0.12, 900);
      tone(context, master, now + 0.04, 0.86, 125, 880, 0.045, "sawtooth");
      tone(context, master, now + 0.12, 0.72, 840, 1_460, 0.02, "sine");
      impactDelay = 1.02;
      impactStrength = 1.2;
    } else if (style === "cruiser") {
      for (let index = 0; index < 4; index += 1) {
        const start = now + index * 0.15;
        noise(context, master, start, 0.11, 0.09, 1_800);
        tone(context, master, start, 0.12, 170, 72, 0.055, "square");
      }
      impactDelay = 1.13;
      impactStrength = 0.92;
    } else if (style === "submarine") {
      tone(context, master, now, 0.28, 820, 620, 0.05, "sine");
      tone(context, master, now + 0.34, 0.75, 72, 116, 0.055, "sine");
      noise(context, master, now + 0.28, 0.9, 0.025, 700);
      impactDelay = 1.3;
      impactStrength = 1.16;
    } else {
      for (let index = 0; index < 6; index += 1) {
        const start = now + index * 0.075;
        tone(context, master, start, 0.58, 260 + index * 50, 1_180, 0.018, "sawtooth");
      }
    }
    impact(context, master, now + impactDelay, result, impactStrength);
    if (sunk) breakAndSink(context, master, now + impactDelay + 0.16);

    window.setTimeout(
      () => {
        activeEffectMasters.delete(master);
        try {
          master.disconnect();
        } catch {
          // Muting or finishing the battle may have disconnected it already.
        }
      },
      sunk ? 4_200 : 2_800,
    );
  } catch {
    // Browsers may deny Web Audio until a user gesture; visuals must remain unaffected.
  }
}
